import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { AppStateType } from "../../redux/reducers";
import styled from "styled-components/macro";
import { Helmet } from "react-helmet-async";
import {
  Grid,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Divider as MuiDivider,
  Typography,
  Box,
  Paper,
  makeStyles,
  Button,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  Theme
} from "@material-ui/core";
import { spacing } from "@material-ui/system";
import { useTranslation } from "react-i18next";
import axios from "../../utils/axios";
import { Formik, Form } from "formik";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import Swal from "sweetalert2";

const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const useStyles = makeStyles((theme: Theme) => ({
  root: {
      marginBottom: "20px",
      marginTop: "20px",
      padding: "20px",
      flexGrow: 1,
  },
  large: {
      width: theme.spacing(15),
      height: theme.spacing(15),
      marginBottom: "10px",
      margin: "0 auto",
  },
  marginTop: {
      marginTop: "10px",
  },
  center: {
      width: "100%",
      textAlign: "center",
  },
  button: {
      background: "linear-gradient(90deg, #84FAB0 0%, #8FEAFE 100%)",
      boxShadow: "0px 0px 14px rgba(53, 64, 82, 0.05)",
  },
  feedback: {
      marginTop: theme.spacing(2),
      marginLeft: theme.spacing(4),
      marginRight: theme.spacing(4),
  },
  alert: {
      backgroundColor: "#354557",
      color: "#cfcfcf"
  },
  labelWrapper: {
    [theme.breakpoints.up("xs")]: {
      display: "block",
      marginTop: "10px"
    },
    [theme.breakpoints.up("sm")]: {
      display: "inline",
      marginTop: "0px"
    }
  }
}));

const categoryOptions = [
  "Service inquiry",
  "Technical issues",
  "Other inquiries"
];

interface Values {
  name: string;
  email: string;
  category: any;
  message: string;
  image: any;
}

const initialValues = {
  name: "",
  email: "",
  category: "Service inquiry",
  message: "",
  image: ""
}

function CustomerSupportPage() {
  const [values, setValues] = useState<Values>(initialValues);
  const userProfile: any = useSelector((state: AppStateType) => state.userReducer.user);
  const classes = useStyles();
  const { t } = useTranslation();

  useEffect(() => {
    if (userProfile) {
      setValues({
        name: `${userProfile.first_name} ${userProfile.last_name}`,
        email: userProfile.email,
        category: "Service inquiry",
        message: "",
        image: ""
      });
    }
}, [userProfile]);

  const categoryMapping = {
    "Service inquiry": t("contactForm_categoryOptions_ServiceInquiry"),
    "Technical issues": t("contactForm_categoryOptions_TechnicalIssues"),
    "Other inquiries": t("contactForm_categoryOptions_OtherInquires")
  }

  const handleSubmit = (e: any) => {
    e.preventDefault();
    // Only validation needed in this form is to check if user added a msg.
    if (values.message.length > 0) {
      sendForm();
    } else {
      showModal(
        t('customerSupportPage_warning'),
        t("contactForm_messageEmptyError")
      )
    }
  }

  const handleCapture = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      if (e.target.files[0]) {
        const file = e.target.files[0];
        console.log("file", file);
        setValues({
          ...values,
          image: file
        });
      }
    }
  };

  const showModal = (title: string, text: string) => {
    Swal.fire({
      title: title,
      text: text,
      confirmButtonText: "CLOSE",
      showCancelButton: false,
      reverseButtons: true,
      width: '400px',
      padding: '20px 10px',
      backdrop: 'transparent'
    })
  }

  const sendForm = async () => {
    const formData = new FormData();
    formData.append(
      "TICKET.attachemnet",
      values.image
    );
    formData.append(
      "email",
      userProfile.email
    );
    formData.append(
      "issue",
      values.category
    );
    formData.append(
      "TICKET.content",
      values.message
    );
    formData.append(
      "TICKET.subject",
      "Support" // required for Hubspot
    );
    formData.append(
      "0-2/company_id",
      userProfile.company
    );
    const portalId = "6123041";
    const formGUID = "261d4412-cc18-4c84-a0e7-1055312cc97f";
    const postData = await axios({
      method: 'post',
      url: `https://forms.hubspot.com/uploads/form/v2/${portalId}/${formGUID}`,
      data: formData,
      headers: {"Content-Type": "multipart/form-data"},
    });

    const status = postData.status;
    if (status >= 200 && status < 300) {
      showModal(
        t('customerSupportPage_thankYou'),
        t('customerSupportPage_requestSuccessfully')
        )
      // set back to initial values
      setValues({
        name: `${userProfile.first_name} ${userProfile.last_name}`,
        email: userProfile.email,
        category: "Service inquiry",
        message: "",
        image: ""
      });
    } else {
      showModal(
        t('customerSupportPage_error'),
        t("signinForm_generalError")
      )
    }
  }

  return (
    <Box p={12}>
      <Helmet title={t("customerSupportPage_title")} />
      <Typography variant="h3" gutterBottom display="inline">
        {t("customerSupportPage_title")}
      </Typography>
      <Divider my={6} />
      <Paper className={classes.root}>
        <Typography gutterBottom variant="h4">
          {t("contactForm_title")}
        </Typography>
        <Typography gutterBottom variant="subtitle1">
          {t("contactForm_subtitle1")} <br/>
          {t("contactForm_subtitle2")}
        </Typography>
        <Formik initialValues={values} onSubmit={sendForm}>
          <Form onSubmit={handleSubmit}>
            <Grid container spacing={5}>
              <Grid item xs={12}>
                <Grid direction="row" container spacing={5}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      className={classes.marginTop}
                      fullWidth
                      label={t("signupForm_signUp_nameLabel")}
                      id="name"
                      value={values.name}
                      disabled
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      className={classes.marginTop}
                      fullWidth
                      label={t("signinForm_emailLabel_textfield")}
                      id="email"
                      value={values.email}
                      disabled
                    />
                  </Grid>
                </Grid>
                <Grid direction="row" container spacing={5}>
                  <Grid item xs={12}>
                    <InputLabel style={{marginTop: "10px"}}>{t("contactForm_category")} *</InputLabel>
                    <Select
                      fullWidth
                      required
                      value={values.category}
                      onChange={(event: React.ChangeEvent<{value: unknown}>) => {
                        setValues({...values, category: event.target.value})
                      }}
                    >
                      {categoryOptions.map((item, index) => {
                        const value = typeof item === "string" ? item : "";
                        return (
                          <MenuItem value={value} key={index}>
                            {(categoryMapping as any)[value] ?? ""}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </Grid>
                </Grid>
                <Grid direction="row" container spacing={5}>
                  <Grid item xs={12}>
                    <TextField
                      className={classes.marginTop}
                      fullWidth
                      label={`${t("contactForm_messageLabel")} *`}
                      id="message"
                      value={values.message}
                      onChange={(event: React.ChangeEvent<{value: string}>) => {
                        setValues({...values, message: event.target.value})
                      }}
                      multiline
                      rows={8}
                    />
                  </Grid>
                </Grid>
                <Grid direction="row" container spacing={5}>
                  <Grid item xs={12}>
                    <input
                      id="button-file"
                      type="file"
                      accept="image/*"
                      style={{display: "none"}}
                      onChange={handleCapture}
                    />
                    <Box mb={2} mt={2}>
                      <InputLabel htmlFor="button-file">
                        <Button
                          variant="contained"
                          className={classes.button}
                          component="span"
                          style={{marginRight: "8px"}}
                        >
                          <CloudUploadIcon style={{paddingRight:"0.2em"}} />{" "}
                          {t("contactForm_chooseImg")}
                        </Button>
                        <span className={classes.labelWrapper}>
                          {(values?.image !== "") ? values.image.name : t("contactForm_attachBtnLabel")}
                        </span>
                      </InputLabel>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Button
              type="submit"
              variant="contained"
              className={classes.button}
              style={{ marginTop: "20px" }}
            >
              {t("contactForm_submitBtn")}
            </Button>
          </Form>
        </Formik>
      </Paper>
    </Box>
  );
}

export default CustomerSupportPage;
